import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, type ViewInstance, type TextInputInstance } from 'react-native';

import { Portal } from '@/components/ui';
import { useBankAccounts, useGlCodes } from '@/hooks/use-accounting';
import type { BankAccount, GlCode } from '@/types/models';
import { bankParentGlIdSet } from '@/utils/bank-parent-gl';

export interface GlCodeTypeaheadProps {
	/** The currently selected GL code id, or undefined / null. */
	value: string | null | undefined;
	onSelect: (glCode: GlCode | null) => void;
	/** Free-text override. If omitted the component manages its own state. */
	value_text?: string;
	onChangeText?: (text: string) => void;
	/** Notify when the user committed a match via Tab. The browser advances
	 * focus naturally on Tab; this callback exists for callers that also want
	 * to know a commit happened (e.g. to flag dirtiness). */
	onTabAdvance?: () => void;
	placeholder?: string;
	autoFocus?: boolean;
	testID?: string;
	inputRef?: React.RefObject<TextInputInstance | null>;
	disabled?: boolean;
	/** Style override for the bare text input — used when this is dropped
	 * into a grid cell that supplies its own border. */
	inputStyle?: any;
	/** Initial visible text for an uncontrolled instance (edit prefill): seeds
	 * the input with an already-committed code so the clear-on-empty effect
	 * doesn't wipe a prefilled `value` on mount. */
	seedText?: string;
	/** Restrict pickable codes, e.g. to income codes for a credit's contra leg. */
	filter?: (glCode: GlCode) => boolean;
}

const defaultMatch = (g: GlCode, q: string): boolean => {
	const code = (g.code ?? '').toLowerCase();
	if (code.startsWith(q)) return true;
	const name = (g.name ?? '').toLowerCase();
	if (name.includes(q)) return true;
	const path = (g.fullPath ?? '').toLowerCase();
	return path.includes(q);
};

const display = (g: GlCode): string => `${g.code} — ${g.name}`;

/**
 * GL-code autocomplete tuned for keyboard-driven journal entry. Mirrors
 * `AccountTypeahead`: prefix-match on code, then substring on name/fullPath.
 * ArrowUp/Down moves highlight; Tab commits and lets the browser advance
 * focus; Enter is intentionally unbound (form-level submit handler owns it);
 * Escape closes the dropdown.
 *
 * The dropdown renders through a Portal at fixed viewport coordinates so it
 * cannot be clipped by a containing grid/scroll view.
 */
export const GlCodeTypeahead: React.FC<GlCodeTypeaheadProps> = ({
	value,
	onSelect,
	value_text,
	onChangeText,
	onTabAdvance,
	placeholder = 'GL code…',
	autoFocus,
	testID,
	inputRef,
	disabled,
	inputStyle,
	seedText,
	filter,
}) => {
	const isControlled = value_text !== undefined;
	const [internalText, setInternalText] = useState(() => seedText ?? '');
	const text = isControlled ? (value_text as string) : internalText;
	const setText = useCallback((t: string) => {
		if (!isControlled) setInternalText(t);
		onChangeText?.(t);
	}, [isControlled, onChangeText]);

	const [highlight, setHighlight] = useState(0);
	const [open, setOpen] = useState(false);
	const wrapRef = useRef<ViewInstance>(null);
	const ownInputRef = useRef<TextInputInstance>(null);
	const effectiveInputRef = inputRef ?? ownInputRef;
	const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; width: number } | null>(null);

	const { data: glResp } = useGlCodes({ activeOnly: true, limit: 1000 });
	// The GL chart is global, so bank-parent detection (#357) is global too:
	// pull every bank account's GL and drop any GL that is a bank-parent (a bank
	// GL with sub-account children) from the pickable matches — postings must
	// target a leaf sub-account, never the roll-up parent.
	const { data: bankResp } = useBankAccounts({ limit: 500 });
	const glCodes = useMemo<GlCode[]>(() => (glResp?.data ?? []) as GlCode[], [glResp]);
	const parentGlIds = useMemo(() => {
		const bankGlIds = new Set(
			((bankResp?.data ?? []) as BankAccount[]).map((b) => b.glCodeId).filter((x): x is string => !!x),
		);
		return bankParentGlIdSet(glCodes, bankGlIds);
	}, [glCodes, bankResp]);

	// Reflect the selected value into the visible text when uncontrolled — the
	// parent may set `value` via state restoration (e.g. cell-level keep-on-blur).
	// Only when `value` CHANGES (or the chart finishes loading): keyed on the text
	// too, it reset every keystroke, so a committed code could never be retyped.
	const reflectedValue = useRef<string | null>(null);
	useEffect(() => {
		if (isControlled) return;
		if (!value) { reflectedValue.current = null; return; }
		if (reflectedValue.current === value) return;
		const found = glCodes.find((g) => g.id === value);
		if (!found) return;
		reflectedValue.current = value;
		setInternalText(display(found));
	}, [value, glCodes, isControlled]);

	const matches = useMemo(() => {
		const q = text.trim().toLowerCase();
		if (!q) return [] as GlCode[];
		// If text already equals the display of the selected value, hide the
		// dropdown — the user is parked on a committed cell.
		if (value) {
			const sel = glCodes.find((g) => g.id === value);
			if (sel && display(sel).toLowerCase() === q) return [];
		}
		return glCodes
			.filter((g) => !parentGlIds.has(g.id) && (!filter || filter(g)) && defaultMatch(g, q))
			.slice(0, 12);
	}, [glCodes, text, value, parentGlIds, filter]);

	useEffect(() => { setHighlight(0); }, [text]);

	// Controlled text: clear the selection when the parent blanks the cell. Uncontrolled
	// text releases it in onChangeText instead — as an effect it also fired for text that
	// was blank only because it hadn't been filled yet, wiping a value the PARENT had just
	// set (chart setup's saved AR/AP pointers, GL report's preselected account).
	useEffect(() => {
		if (isControlled && !text.trim() && value) onSelect(null);
	}, [isControlled, text, value, onSelect]);

	const measureAnchor = useCallback(() => {
		const node = wrapRef.current as unknown as {
			getBoundingClientRect?: () => DOMRect;
			measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void;
		} | null;
		if (!node) return;
		if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
			const r = node.getBoundingClientRect();
			setAnchorRect({ top: r.bottom, left: r.left, width: r.width });
		} else if (typeof node.measureInWindow === 'function') {
			node.measureInWindow((x, y, w, h) => setAnchorRect({ top: y + h, left: x, width: w }));
		}
	}, []);

	useEffect(() => {
		if (!open) return;
		measureAnchor();
		if (Platform.OS !== 'web') return;
		const onMove = () => measureAnchor();
		window.addEventListener('scroll', onMove, true);
		window.addEventListener('resize', onMove);
		return () => {
			window.removeEventListener('scroll', onMove, true);
			window.removeEventListener('resize', onMove);
		};
	}, [open, measureAnchor, matches.length]);

	const commit = (g: GlCode, advance: boolean) => {
		setText(display(g));
		onSelect(g);
		setOpen(false);
		if (advance) onTabAdvance?.();
	};

	return (
		<View ref={wrapRef} style={styles.wrap}>
			<TextInput
				ref={effectiveInputRef}
				value={text}
				onChangeText={(v) => {
					setText(v);
					setOpen(true);
					// Typing over a committed code releases it, so the selection can never
					// disagree with the text on screen. Picking a match re-commits.
					if (value) {
						const sel = glCodes.find((g) => g.id === value);
						if (!sel || display(sel) !== v) onSelect(null);
					}
				}}
				autoFocus={autoFocus}
				autoCorrect={false}
				autoCapitalize="none"
				editable={!disabled}
				placeholder={placeholder}
				placeholderTextColor="#b0bec5"
				style={[styles.input, inputStyle, disabled && styles.inputDisabled]}
				onFocus={() => setOpen(true)}
				onBlur={() => setTimeout(() => setOpen(false), 120)}
				testID={testID}
				onKeyPress={(e: any) => {
					const key = e.nativeEvent?.key as string | undefined;
					if (key === 'ArrowDown') {
						e.preventDefault?.();
						setHighlight((h) => Math.min(matches.length - 1, h + 1));
					} else if (key === 'ArrowUp') {
						e.preventDefault?.();
						setHighlight((h) => Math.max(0, h - 1));
					} else if (key === 'Tab') {
						// Commit highlighted match but DON'T preventDefault so the
						// browser moves focus to the next cell natively — that's the
						// keyboard-driven accounting UX from AccountTypeahead.
						const top = matches[highlight] ?? matches[0];
						if (top) commit(top, true);
					} else if (key === 'Enter') {
						const top = matches[highlight] ?? matches[0];
						if (top) {
							e.preventDefault?.();
							commit(top, false);
						}
					} else if (key === 'Escape') {
						setOpen(false);
					}
				}}
			/>
			{open && matches.length > 0 && anchorRect && (
				<Portal>
					<View
						testID={testID ? `${testID}-dropdown` : 'gl-typeahead-dropdown'}
						style={{
							position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
							top: anchorRect.top + 2,
							left: anchorRect.left,
							width: Math.max(anchorRect.width, 320),
							zIndex: 99999,
							maxHeight: 320,
						}}
					>
						<View style={styles.dropdown}>
							<ScrollView style={{ maxHeight: 320 }} keyboardShouldPersistTaps="always" nestedScrollEnabled>
								{matches.map((g, idx) => (
									<Pressable
										key={g.id}
										onPress={() => commit(g, true)}
										style={[styles.option, idx === highlight && styles.optionActive]}
									>
										<Text style={styles.optCode}>{g.code}</Text>
										<Text style={styles.optName} numberOfLines={1}>{g.name}</Text>
										<Text style={styles.optType}>{g.accountType}</Text>
									</Pressable>
								))}
							</ScrollView>
						</View>
					</View>
				</Portal>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	wrap: { position: 'relative', flex: 1, alignSelf: 'stretch' },
	input: {
		borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		padding: 10, fontSize: 14, backgroundColor: '#fff',
		width: '100%',
	},
	inputDisabled: { backgroundColor: '#f5f5f5', color: '#9e9e9e' },
	dropdown: {
		backgroundColor: '#fff',
		borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 12, shadowOffset: { width: 0, height: 6 },
		elevation: 24,
		overflow: 'hidden',
	},
	option: {
		flexDirection: 'row', alignItems: 'center', gap: 10,
		paddingVertical: 8, paddingHorizontal: 10,
		borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: '#eee',
	},
	optionActive: { backgroundColor: '#e3f2fd' },
	optCode: { fontFamily: 'monospace', fontWeight: '600', color: '#1a237e', minWidth: 64 },
	optName: { color: '#263238', fontSize: 13, fontWeight: '600', flex: 1 },
	optType: { color: '#90a4ae', fontSize: 11, fontFamily: 'monospace' },
});
